import { ChangeDetectionStrategy, Component, output, signal, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-aula2-section',
  standalone: true,
  imports: [CommonModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <section class="py-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto space-y-8">
      <!-- Breadcrumb Navigation & Top Action Bar -->
      <div class="flex flex-wrap items-center justify-between gap-4">
        <button 
          type="button" 
          (click)="navigateBack.emit()" 
          class="inline-flex items-center space-x-2 px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-semibold text-[#F27D26] border border-white/10 transition-all cursor-pointer">
          <span class="material-symbols-outlined text-base">arrow_back</span>
          <span>Voltar ao Menu de Aulas</span>
        </button>

        <div class="flex flex-wrap items-center gap-3">
          <!-- View Switcher Tabs -->
          <div class="flex items-center space-x-1 bg-[#121214] p-1.5 rounded-xl border border-white/10">
            <button 
              type="button"
              (click)="onSelectView('slides')"
              [class]="activeView() === 'slides' 
                ? 'bg-[#F27D26] text-black font-bold shadow-md shadow-[#F27D26]/20' 
                : 'text-zinc-400 hover:text-white hover:bg-white/5'"
              class="px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-base">slideshow</span>
              <span>Slides</span>
            </button>

            <button 
              type="button"
              (click)="onSelectView('activity')"
              [class]="activeView() === 'activity' 
                ? 'bg-[#F27D26] text-black font-bold shadow-md shadow-[#F27D26]/20' 
                : 'text-zinc-400 hover:text-white hover:bg-white/5'"
              class="px-3.5 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-base">assignment</span>
              <span>Folha de Atividades</span>
            </button>
          </div>
        </div>
      </div>

      <!-- VIEW 1: INLINE SLIDES PRESENTATION CARD -->
      @if (activeView() === 'slides') {
        <div class="bg-[#121214] rounded-2xl border border-white/10 shadow-2xl p-6 sm:p-10 min-h-[580px] flex flex-col justify-between relative overflow-hidden">
          <!-- Slide Glow Accent -->
          <div class="absolute -top-24 -right-24 w-80 h-80 bg-[#F27D26]/10 rounded-full blur-3xl pointer-events-none"></div>

          <!-- Top Banner Bar -->
          <div class="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-white/10">
            <div class="flex items-center space-x-2">
              <span class="material-symbols-outlined text-[#F27D26] text-xl">slideshow</span>
              <span class="text-xs font-mono font-semibold text-zinc-300">Modo de Leitura Integrado</span>
            </div>
            
            <button 
              type="button" 
              (click)="openPresentationModal()" 
              class="px-3 py-1.5 rounded-lg bg-[#F27D26]/15 hover:bg-[#F27D26]/25 text-[#F27D26] border border-[#F27D26]/30 text-xs font-bold transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-sm">present_to_all</span>
              <span>Apresentação Imersiva</span>
            </button>
          </div>

          <!-- SLIDES CONTENT -->
          <div class="my-6">
            <!-- SLIDE 1 -->
            @if (currentSlide() === 0) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">school</span>
                  <span>AULA 2 • EMPREENDEDORISMO EM REDES</span>
                </span>

                <div>
                  <h1 class="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
                    A Jornada do Empreendedor em TI
                  </h1>
                  <p class="text-lg text-zinc-400 font-medium mt-1">
                    Como o conhecimento técnico resolve problemas reais de mercado.
                  </p>
                </div>

                <div class="bg-[#0A0A0B] rounded-xl p-6 border-l-4 border-amber-500 border-y border-r border-white/10 space-y-3">
                  <h3 class="text-lg font-bold text-white flex items-center space-x-2">
                    <span>🙋‍♂️</span>
                    <span>Conheça o João:</span>
                  </h3>
                  <p class="text-zinc-300 text-sm leading-relaxed">
                    O João vai se formar em <strong>Técnico em Redes de Computadores</strong> no fim do ano. Mas já domina crimpagem, MikroTik, Switches e Windows. Hoje ele recebeu uma ligação do <strong>Sr. Roberto</strong>, dono da <em>Logística Expressa</em>, dizendo em desespero: <em class="text-amber-400 font-semibold">"Minha internet caiu e estou perdendo R$ 1.500 por hora!"</em>
                  </p>
                </div>

                <p class="text-zinc-300 text-sm leading-relaxed">
                  <strong>Como o João pode ajudar?</strong> Acompanhe os slides e veja como transformar esse problema em uma oportunidade de negócio!
                </p>
              </div>
            }

            <!-- SLIDE 2 -->
            @if (currentSlide() === 1) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">search</span>
                  <span>PASSO 1 • O DIAGNÓSTICO</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  O Dilema do Sr. Roberto
                </h2>

                <div class="bg-indigo-950/40 rounded-xl p-5 border border-indigo-500/30 space-y-2">
                  <h3 class="text-base font-bold text-indigo-300 flex items-center space-x-2">
                    <span class="material-symbols-outlined text-indigo-400 text-lg">help</span>
                    <span>Pergunta de Reflexão Rápida:</span>
                  </h3>
                  <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed">
                    A internet da Logística Expressa cai toda terça e quinta. Os caminhões ficam parados no pátio porque a Nota Fiscal Eletrônica não emite. Qual é o REAL problema que precisa ser resolvido?
                  </p>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] rounded-xl p-5 border-l-4 border-red-500 border-y border-r border-white/10 space-y-3">
                    <h3 class="text-base font-bold text-red-400 flex items-center space-x-2">
                      <span>❌</span>
                      <span>Visão Reativa (Técnico Tradicional)</span>
                    </h3>
                    <p class="text-xs sm:text-sm italic text-zinc-300">
                      "Vou lá reiniciar o modem residencial da operadora e trocar a senha do Wi-Fi."
                    </p>
                    <p class="text-xs text-zinc-400 pt-2 border-t border-white/5">
                      Resultado: Na terça seguinte a rede cai de novo e a empresa perde mais R$ 3.000.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-5 border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-3">
                    <h3 class="text-base font-bold text-emerald-400 flex items-center space-x-2">
                      <span>✅</span>
                      <span>Visão Empreendedora (O João)</span>
                    </h3>
                    <p class="text-xs sm:text-sm italic text-zinc-300">
                      "Preciso garantir que a emissão de notas NUNCA pare, criando tolerância a falhas na rede."
                    </p>
                    <p class="text-xs text-zinc-400 pt-2 border-t border-white/5">
                      Resultado: O cliente economiza milhares de reais e fecha um contrato mensal de suporte.
                    </p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 3 -->
            @if (currentSlide() === 2) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">key</span>
                  <span>PASSO 2 • CONCEITO CHAVE</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  O que é Empreender em Redes?
                </h2>

                <p class="text-zinc-300 text-sm leading-relaxed">
                  Empreender NÃO é apenas abrir uma empresa. É a habilidade de conectar uma <strong>DOR REAL DE NEGÓCIO</strong> a uma <strong>SOLUÇÃO DE INFRAESTRUTURA</strong>.
                </p>

                <div class="bg-[#F27D26]/10 rounded-xl p-4 border border-[#F27D26]/30 text-center font-mono text-xs sm:text-sm font-semibold text-[#F27D26] leading-relaxed">
                  DOR DO CLIENTE (Paralisação/Prejuízo) ➔ SOLUÇÃO DE REDE (Failover/Redundância) = PROPOSTA DE VALOR 💰
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] rounded-xl p-5 border border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-white flex items-center space-x-2">
                      <span>🍕</span>
                      <span>Exemplo Fora da TI (Pizzaria)</span>
                    </h3>
                    <p class="text-xs text-zinc-300"><strong>Dor:</strong> Pizzas chegam frias e atrasadas no sábado.</p>
                    <p class="text-xs text-zinc-300"><strong>Solução:</strong> Bag térmica com aquecimento USB e roteamento de entregadores via App.</p>
                    <p class="text-xs text-emerald-400 font-semibold pt-1 border-t border-white/5"><strong>Valor:</strong> Pizza quente = Cliente satisfeito e fiel.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-5 border border-white/10 space-y-2">
                    <h3 class="text-base font-bold text-white flex items-center space-x-2">
                      <span>🌐</span>
                      <span>Exemplo em Redes (Supermercado)</span>
                    </h3>
                    <p class="text-xs text-zinc-300"><strong>Dor:</strong> Caixas travam ao passar compras no horário de pico.</p>
                    <p class="text-xs text-zinc-300"><strong>Solução:</strong> Roteador MikroTik com link duplo (Fibra + Chip 4G) e VLAN isolada para os Caixas.</p>
                    <p class="text-xs text-emerald-400 font-semibold pt-1 border-t border-white/5"><strong>Valor:</strong> Fila rápida = Zero perda de vendas.</p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 4 -->
            @if (currentSlide() === 3) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">psychology</span>
                  <span>PASSO 3 • MENTALIDADE</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Qual Profissional Você Quer Ser?
                </h2>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div class="bg-[#0A0A0B] rounded-xl p-5 border border-white/10 space-y-3">
                    <div class="text-3xl mb-1">🛠️</div>
                    <h3 class="text-base font-bold text-white">O Técnico Operacional</h3>
                    <ul class="space-y-2 text-xs text-zinc-400">
                      <li class="flex items-start space-x-1.5">
                        <span class="text-zinc-500">•</span>
                        <span>Foca apenas no <strong>Equipamento / Cabo</strong>.</span>
                      </li>
                      <li class="flex items-start space-x-1.5">
                        <span class="text-zinc-500">•</span>
                        <span>Espera o chamado abrir para consertar.</span>
                      </li>
                      <li class="flex items-start space-x-1.5">
                        <span class="text-zinc-500">•</span>
                        <span>Cobra por hora de visita (Ganho único).</span>
                      </li>
                      <li class="flex items-start space-x-1.5">
                        <span class="text-zinc-500">•</span>
                        <span>Diz: <em>"O problema é da sua operadora."</em></span>
                      </li>
                    </ul>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-5 border border-[#F27D26]/50 space-y-3 shadow-lg shadow-[#F27D26]/5">
                    <div class="text-3xl mb-1">🚀</div>
                    <h3 class="text-base font-bold text-[#F27D26]">O Especialista Empreendedor</h3>
                    <ul class="space-y-2 text-xs text-zinc-300">
                      <li class="flex items-start space-x-1.5">
                        <span class="text-[#F27D26]">•</span>
                        <span>Foca na <strong>Continuidade do Negócio</strong>.</span>
                      </li>
                      <li class="flex items-start space-x-1.5">
                        <span class="text-[#F27D26]">•</span>
                        <span>Monitora proativamente para evitar paradas.</span>
                      </li>
                      <li class="flex items-start space-x-1.5">
                        <span class="text-[#F27D26]">•</span>
                        <span>Cobra um valor mensal fixo (Contrato MSP).</span>
                      </li>
                      <li class="flex items-start space-x-1.5">
                        <span class="text-[#F27D26]">•</span>
                        <span>Diz: <em>"Instalei um backup automático para você não parar."</em></span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 5 -->
            @if (currentSlide() === 4) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">account_tree</span>
                  <span>PASSO 4 • A SOLUÇÃO TÉCNICA DO JOÃO</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  Visualizando a Transformação da Rede
                </h2>
                <p class="text-zinc-400 text-xs sm:text-sm">
                  Veja como o João desenhou a solução técnica para a <em>Logística Expressa</em>:
                </p>

                <div class="space-y-4">
                  <div class="bg-[#0A0A0B] rounded-xl p-5 border-l-4 border-red-500 border-y border-r border-white/10 space-y-2">
                    <h3 class="text-sm font-bold text-red-400">❌ Rede Antiga (Ponto Único de Falha):</h3>
                    <div class="bg-[#121214] p-3 rounded-lg border border-red-500/20 font-mono text-xs text-red-300 text-center">
                      [ Modem Residencial ] ── (Wi-Fi Comum) ──> [ Caixas, Escritório e Celulares Juntos ]
                    </div>
                    <p class="text-xs text-zinc-400">Se a operadora cai ou o Wi-Fi engasga, a empresa inteira para!</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-5 border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-2">
                    <h3 class="text-sm font-bold text-emerald-400">✅ Nova Arquitetura Proposta pelo João (Tolerante a Falhas):</h3>
                    <div class="bg-[#121214] p-3 rounded-lg border border-emerald-500/20 font-mono text-xs text-emerald-300 text-center leading-relaxed">
                      [ Link 1: Fibra ] ──┐<br>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;├─> [ Roteador MikroTik / Failover ] ──> [ VLAN NFs ] + [ Wi-Fi Visitantes ]<br>
                      [ Link 2: 4G/5G ] ──┘
                    </div>
                    <p class="text-xs text-zinc-400">Se a Fibra cair, o 4G assume em 2 segundos de forma invisível!</p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 6 -->
            @if (currentSlide() === 5) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">map</span>
                  <span>PASSO 5 • MAPA DE OPORTUNIDADES</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  6 Caminhos para Lucrar com Redes
                </h2>

                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 space-y-1.5">
                    <div class="text-2xl">🌐</div>
                    <h3 class="font-bold text-white text-sm">1. ISPs Regionais</h3>
                    <p class="text-xs text-zinc-400">Provedores de Fibra/Rádio e redes neutras em bairros/zonas rurais.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 space-y-1.5">
                    <div class="text-2xl">☁️</div>
                    <h3 class="font-bold text-white text-sm">2. Cloud & VPNs</h3>
                    <p class="text-xs text-zinc-400">Migração de servidores para nuvem e conexão segura para Home Office.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 space-y-1.5">
                    <div class="text-2xl">🛡️</div>
                    <h3 class="font-bold text-white text-sm">3. Cibersegurança</h3>
                    <p class="text-xs text-zinc-400">Firewalls corporativos, proteção contra ransomware e LGPD.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 space-y-1.5">
                    <div class="text-2xl">🤖</div>
                    <h3 class="font-bold text-white text-sm">4. IoT & Câmeras IP</h3>
                    <p class="text-xs text-zinc-400">Redes para automação industrial, agronegócio e CFTV IP.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 space-y-1.5">
                    <div class="text-2xl">📡</div>
                    <h3 class="font-bold text-white text-sm">5. Wi-Fi 6 Corporativo</h3>
                    <p class="text-xs text-zinc-400">Projetos para hotéis, eventos, galpões e coworkings.</p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-xl p-4 border border-white/10 space-y-1.5">
                    <div class="text-2xl">🔄</div>
                    <h3 class="font-bold text-white text-sm">6. Contratos MSP</h3>
                    <p class="text-xs text-zinc-400">Gerenciamento mensal da TI de empresas com receita recorrente.</p>
                  </div>
                </div>
              </div>
            }

            <!-- SLIDE 7 -->
            @if (currentSlide() === 6) {
              <div class="space-y-6 animate-in fade-in duration-300">
                <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                  <span class="material-symbols-outlined text-sm">verified</span>
                  <span>SÍNTESE DA AULA</span>
                </span>

                <h2 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                  A Grande Lição do Dia
                </h2>

                <div class="bg-gradient-to-r from-[#F27D26]/20 via-[#F27D26]/10 to-transparent rounded-2xl p-6 sm:p-8 border border-[#F27D26]/40 text-center">
                  <h3 class="text-lg sm:text-xl font-extrabold text-[#F27D26] leading-relaxed">
                    "Empreender em Redes não é vender equipamentos, cabos ou configurações.<br><br>É resolver problemas que fazem alguém perder tempo, dinheiro ou oportunidades."
                  </h3>
                </div>

                <div class="bg-[#0A0A0B] rounded-xl p-6 border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-3">
                  <h3 class="text-base font-bold text-white flex items-center space-x-2">
                    <span>🚀</span>
                    <span>Próximo Passo:</span>
                  </h3>
                  <p class="text-zinc-300 text-xs sm:text-sm leading-relaxed">
                    Agora é a sua vez! Abra a <strong>Folha de Atividades</strong>, leia o estudo de caso completo, cote os equipamentos reais na internet, resolva o desafio do cabeamento físico e monte a proposta comercial para o Sr. Roberto!
                  </p>
                  <div class="pt-2">
                    <button 
                      type="button" 
                      (click)="onSelectView('activity')" 
                      class="px-4 py-2 rounded-lg bg-[#F27D26] text-black font-bold text-xs hover:bg-[#e06c17] transition-all flex items-center space-x-1.5 cursor-pointer">
                      <span class="material-symbols-outlined text-base">assignment</span>
                      <span>Ir para Folha de Atividades</span>
                    </button>
                  </div>
                </div>
              </div>
            }
          </div>

          <!-- Slide Controls Footer -->
          <div class="pt-4 border-t border-white/10 flex items-center justify-between">
            <button 
              type="button" 
              (click)="prevSlide()" 
              [disabled]="currentSlide() === 0"
              class="px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 text-xs font-semibold text-white border border-white/10 transition-all flex items-center space-x-1 cursor-pointer">
              <span class="material-symbols-outlined text-base">chevron_left</span>
              <span>Anterior</span>
            </button>

            <div class="flex items-center space-x-3">
              <span class="text-xs text-zinc-400 font-mono font-medium">
                Slide {{ currentSlide() + 1 }} de {{ totalSlides }}
              </span>
              <div class="w-24 sm:w-36 h-2 bg-white/10 rounded-full overflow-hidden hidden sm:block">
                <div 
                  class="h-full bg-[#F27D26] transition-all duration-300"
                  [style.width.%]="((currentSlide() + 1) / totalSlides) * 100">
                </div>
              </div>
            </div>

            <button 
              type="button" 
              (click)="nextSlide()" 
              [disabled]="currentSlide() === totalSlides - 1"
              class="px-4 py-2 rounded-xl bg-[#F27D26] hover:bg-[#e06c17] disabled:opacity-30 disabled:hover:bg-[#F27D26] text-xs font-bold text-black transition-all flex items-center space-x-1 cursor-pointer">
              <span>Próximo</span>
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      }

      <!-- VIEW 2: FOLHA DE ATIVIDADES -->
      @if (activeView() === 'activity') {
        <div class="bg-[#121214] rounded-2xl border border-white/10 shadow-2xl p-6 sm:p-10 space-y-8">
          <!-- Activity Header -->
          <div class="flex flex-wrap items-center justify-between gap-4 pb-6 border-b border-white/10">
            <div>
              <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider mb-2">
                <span class="material-symbols-outlined text-sm">edit_note</span>
                <span>DESAFIO PRÁTICO</span>
              </span>
              <h1 class="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
                📝 Folha de Atividades & Desafio de Campo
              </h1>
            </div>

            <button 
              type="button" 
              (click)="printActivity()" 
              class="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs shadow-lg shadow-emerald-600/20 transition-all flex items-center space-x-1.5 cursor-pointer">
              <span class="material-symbols-outlined text-base">print</span>
              <span>Imprimir Folha</span>
            </button>
          </div>

          <!-- MÓDULO A -->
          <div class="space-y-4">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO A: Alinhamento de Conceitos</span>
            </h2>

            <p class="text-xs sm:text-sm text-zinc-300 leading-relaxed text-justify">
              <strong>1. Complete o quadro identificando a DOR REAL do cliente e a SOLUÇÃO TÉCNICA EMPREENDEDORA:</strong>
            </p>

            <div class="overflow-x-auto">
              <table class="w-full text-left text-xs border-collapse border border-white/10 rounded-xl overflow-hidden">
                <thead>
                  <tr class="bg-[#0A0A0B] text-[#F27D26] font-mono border-b border-white/10">
                    <th class="p-3 border-r border-white/10">Cenário</th>
                    <th class="p-3 border-r border-white/10">Pedido do Cliente</th>
                    <th class="p-3 border-r border-white/10">Dor Real de Negócio</th>
                    <th class="p-3">Solução Empreendedora de Rede</th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-white/10 text-zinc-300">
                  <tr class="bg-[#121214]">
                    <td class="p-3 border-r border-white/10 font-bold text-white">Escritório de Contabilidade</td>
                    <td class="p-3 border-r border-white/10 italic text-zinc-400">"Preciso de um cabo de rede novo."</td>
                    <td class="p-3 border-r border-white/10">
                      <textarea [(ngModel)]="modA_q1_p1" rows="2" placeholder="Escreva a dor real..." class="w-full bg-[#0A0A0B] border border-white/10 rounded p-2 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
                    </td>
                    <td class="p-3">
                      <textarea [(ngModel)]="modA_q1_p2" rows="2" placeholder="Escreva a solução..." class="w-full bg-[#0A0A0B] border border-white/10 rounded p-2 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
                    </td>
                  </tr>
                  <tr class="bg-[#0A0A0B]">
                    <td class="p-3 border-r border-white/10 font-bold text-white">Farmácia 24 Horas</td>
                    <td class="p-3 border-r border-white/10 italic text-zinc-400">"Preciso que venham reiniciar o roteador."</td>
                    <td class="p-3 border-r border-white/10">
                      <textarea [(ngModel)]="modA_q2_p1" rows="2" placeholder="Escreva a dor real..." class="w-full bg-[#121214] border border-white/10 rounded p-2 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
                    </td>
                    <td class="p-3">
                      <textarea [(ngModel)]="modA_q2_p2" rows="2" placeholder="Escreva a solução..." class="w-full bg-[#121214] border border-white/10 rounded p-2 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- MÓDULO B -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO B: Pesquisa Investigativa de Mercado (Fontes Oficiais)</span>
            </h2>

            <p class="text-xs sm:text-sm text-zinc-300 leading-relaxed text-justify">
              Acesse no celular/computador portais oficiais (ex: Anatel, Cetic.br, Teleco, IBGE) e responda:
            </p>

            <div class="space-y-4">
              <div>
                <label class="block text-xs font-bold text-zinc-200 mb-1 text-justify">
                  <strong>2.</strong> Qual a porcentagem aproximada da banda larga fixa no Brasil atendida por <strong>Provedores Regionais (PPPs)</strong> em relação às grandes operadoras?
                </label>
                <input type="text" [(ngModel)]="modB_q2" placeholder="Sua resposta..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-[#F27D26]">
              </div>

              <div>
                <label class="block text-xs font-bold text-zinc-200 mb-1 text-justify">
                  <strong>3.</strong> Qual a tecnologia de transmissão (ex: Fibra FTTH, Rádio) mais usada por esses provedores locais?
                </label>
                <input type="text" [(ngModel)]="modB_q3" placeholder="Sua resposta..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-[#F27D26]">
              </div>

              <div>
                <label class="block text-xs font-bold text-zinc-200 mb-1 text-justify">
                  <strong>4.</strong> Por que os provedores regionais de bairro conseguem competir com grandes multinacionais de telecomunicações no suporte ao cliente?
                </label>
                <textarea [(ngModel)]="modB_q4" rows="3" placeholder="Sua resposta..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
              </div>
            </div>
          </div>

          <!-- MÓDULO C -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO C: Estudo de Caso Prático – Diagnóstico de Negócio</span>
            </h2>

            <div class="bg-[#0A0A0B] rounded-xl p-5 border border-white/10 space-y-3">
              <h3 class="text-base font-bold text-white flex items-center space-x-2">
                <span class="material-symbols-outlined text-[#F27D26] text-lg">description</span>
                <span>Texto para Leitura: O Caso Completo da "Logística Expressa & Cia"</span>
              </h3>

              <p class="text-xs text-zinc-300 leading-relaxed text-justify">
                A <em>Logística Expressa & Cia</em> é uma empresa de médio porte especializada em entregas rápidas para e-commerce regional. Ela possui um galpão principal onde trabalham 12 funcionários administrativos no escritório central, além de 8 operadores no pátio de carregamento e emissão de notas fiscais.
              </p>

              <p class="text-xs text-zinc-300 leading-relaxed text-justify">
                Recently, a empresa expandiu suas operações e passou a rodar um sistema de gestão de estoque 100% baseado na nuvem (Web/SaaS). No entanto, a infraestrutura de redes da empresa foi montada há mais de 6 anos de forma improvisada: há apenas um modem de internet residencial fornecido por uma operadora local, ligado a um roteador Wi-Fi comum no meio do escritório.
              </p>

              <div class="space-y-2 pt-2 border-t border-white/5">
                <h4 class="text-xs font-bold text-red-400 uppercase tracking-wider">O Grande Problema:</h4>
                <p class="text-xs text-zinc-300 leading-relaxed text-justify">
                  Todas as terças e quintas-feiras à tarde, o volume de tráfego na região aumenta e a conexão de internet da empresa oscila severamente ou cai por até 2 horas consecutivas. Quando isso acontece:
                </p>
                <ul class="space-y-1.5 text-xs text-zinc-400 pl-4 list-disc text-justify">
                  <li>Os operadores do pátio <strong>não conseguem emitir as Notas Fiscais Eletrônicas (NF-e)</strong> para liberar os caminhões de entrega.</li>
                  <li>Os motoristas ficam parados no pátio por horas, gerando atrasos nas entregas finais aos clientes.</li>
                  <li>O escritório administrativo fica sem acesso ao sistema de estoque e sem comunicação com os clientes via WhatsApp Web.</li>
                  <li>Estima-se que cada hora de parada gere um prejuízo operacional de <strong class="text-amber-400">R$ 1.500,00</strong> em multas por atraso e horas extras da equipe.</li>
                </ul>
              </div>

              <p class="text-xs text-zinc-300 leading-relaxed pt-2 border-t border-white/5 text-justify">
                O diretor da empresa, Sr. Roberto, procurou um técnico de TI tradicional no mês passado. O técnico apenas foi até lá, trocou a senha do Wi-Fi e disse: <em>"O problema é a sua operadora de internet, quando cair é só tirar o modem da tomada por 10 segundos e ligar de novo."</em> O problema, obviamente, continuou acontecendo semanalmente.
              </p>

              <p class="text-xs text-zinc-300 leading-relaxed font-semibold text-[#F27D26] text-justify">
                Agora, o Sr. Roberto decided contratar a sua equipe de consultoria (onde o João trabalha) para apresentar uma solução definitiva.
              </p>
            </div>

            <div>
              <label class="block text-xs font-bold text-zinc-200 mb-1 text-justify">
                <strong>5. Diagnóstico de Prejuízo:</strong> Sabendo que a internet da empresa cai 2 horas na terça e 2 horas na quinta (4 horas semanais), calcule o prejuízo financeiro acumulado que o Sr. Roberto enfrenta em <strong>um único mês (4 semanas)</strong>:
              </label>
              <textarea [(ngModel)]="modC_q5" rows="2" placeholder="Mostre a conta e a resposta..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2.5 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
            </div>

            <div class="space-y-2">
              <label class="block text-xs font-bold text-zinc-200 text-justify">
                <strong>6. Pesquisa de Equipamentos Reais (Cotação na Internet):</strong>
              </label>
              <p class="text-xs text-zinc-400 text-justify">
                Ajude a equipe a cotar a solução de redundância (Link Duplo/Failover) apresentada nos slides. Pesquise em sites de e-commerce (Mercado Livre, Amazon, Kabum) os preços médios reais dos equipamentos:
              </p>

              <div class="overflow-x-auto">
                <table class="w-full text-left text-xs border-collapse border border-white/10 rounded-xl overflow-hidden">
                  <thead>
                    <tr class="bg-[#0A0A0B] text-[#F27D26] font-mono border-b border-white/10">
                      <th class="p-3 border-r border-white/10">Equipamento Requerido</th>
                      <th class="p-3 border-r border-white/10">Modelo / Marca Sugerida</th>
                      <th class="p-3">Preço Médio Encontrado (R$)</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-white/10 text-zinc-300">
                    <tr class="bg-[#121214]">
                      <td class="p-3 border-r border-white/10 font-medium">1x Roteador com Suporte a Link Duplo (Failover)</td>
                      <td class="p-3 border-r border-white/10 text-zinc-400">Ex: MikroTik hEX / TP-Link Load Balance</td>
                      <td class="p-3">
                        <input type="text" [(ngModel)]="priceEq1" placeholder="R$ 0,00" class="w-full bg-[#0A0A0B] border border-white/10 rounded p-2 text-xs text-white focus:outline-none focus:border-[#F27D26]">
                      </td>
                    </tr>
                    <tr class="bg-[#0A0A0B]">
                      <td class="p-3 border-r border-white/10 font-medium">1x Modem/Roteador 4G/5G de Backup (com chip)</td>
                      <td class="p-3 border-r border-white/10 text-zinc-400">Ex: TP-Link 4G / Huawei / ZLT</td>
                      <td class="p-3">
                        <input type="text" [(ngModel)]="priceEq2" placeholder="R$ 0,00" class="w-full bg-[#121214] border border-white/10 rounded p-2 text-xs text-white focus:outline-none focus:border-[#F27D26]">
                      </td>
                    </tr>
                    <tr class="bg-[#121214] font-bold">
                      <td class="p-3 border-r border-white/10 text-white">VALOR TOTAL DOS EQUIPAMENTOS:</td>
                      <td class="p-3 border-r border-white/10 text-zinc-500">---</td>
                      <td class="p-3">
                        <input type="text" [(ngModel)]="priceTotal" placeholder="R$ Total" class="w-full bg-[#0A0A0B] border border-[#F27D26]/40 rounded p-2 text-xs text-[#F27D26] font-mono font-bold focus:outline-none">
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="space-y-3">
              <label class="block text-xs font-bold text-zinc-200 text-justify">
                <strong>7. Elaboração de Pitch Comercial Imbatível:</strong>
              </label>

              <div class="bg-indigo-950/40 rounded-xl p-4 border border-indigo-500/30 space-y-2">
                <h3 class="text-xs font-bold text-indigo-300 flex items-center space-x-1.5">
                  <span class="material-symbols-outlined text-sm">lightbulb</span>
                  <span>💡 Roteiro de Apoio para Construção do Pitch:</span>
                </h3>
                <p class="text-xs text-zinc-300 text-justify">Para convencer o Sr. Roberto, organize seu texto seguindo estas 4 frases em sequência:</p>
                <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono">
                  <div class="bg-[#0A0A0B] p-2 rounded border border-white/10 text-zinc-300"><strong>Passo 1</strong> - Mostre a Dor</div>
                  <div class="bg-[#0A0A0B] p-2 rounded border border-white/10 text-zinc-300"><strong>Passo 2</strong> - Apresente a Solução</div>
                  <div class="bg-[#0A0A0B] p-2 rounded border border-white/10 text-zinc-300"><strong>Passo 3</strong> - Compare os Valores</div>
                  <div class="bg-[#0A0A0B] p-2 rounded border border-white/10 text-zinc-300"><strong>Passo 4</strong> - Fechamento/Incentivo</div>
                </div>
              </div>

              <p class="text-xs text-zinc-300 text-justify">Escreva abaixo o Pitch Comercial final da sua equipe seguindo o roteiro acima:</p>
              <textarea [(ngModel)]="modC_q7" rows="5" placeholder="Digite o pitch da sua equipe aqui..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
            </div>
          </div>

          <!-- MÓDULO D -->
          <div class="space-y-4 pt-4 border-t border-white/10">
            <h2 class="text-xl font-bold text-[#F27D26] flex items-center space-x-2 border-b border-white/10 pb-2">
              <span>📍 MÓDULO D: A Camada Física e o Valor do Cabeamento Estruturado</span>
            </h2>

            <div class="bg-[#0A0A0B] rounded-xl p-5 border border-white/10 space-y-3">
              <h3 class="text-base font-bold text-white flex items-center space-x-2">
                <span class="material-symbols-outlined text-[#F27D26] text-lg">cable</span>
                <span>📄 Continuação do Caso: O "Ninho de Ratos" na Logística Expressa</span>
              </h3>

              <p class="text-xs text-zinc-300 leading-relaxed text-justify">
                O Sr. Roberto aprovou o orçamento do João para o Link Duplo! Porém, no dia da instalação, João se depara com um novo problema: o local onde o modem antigo ficava era um verdadeiro "ninho de ratos".
              </p>

              <p class="text-xs text-zinc-300 leading-relaxed text-justify">
                Havia três pequenos switches de mesa empilhados, cabos de rede tensionados e misturados com cabos de energia elétrica, conectores remendados com fita isolante e nenhum cabo estava identificado. O Sr. Roberto comentou: <em>"Sempre foi assim. Às vezes a moça da limpeza esbarra com a vassoura ali, um cabo solta e a impressora fiscal para de funcionar por horas até acharmos qual fio soltou."</em>
              </p>

              <p class="text-xs text-zinc-300 leading-relaxed font-semibold text-[#F27D26] text-justify">
                João tem a percepção dele, mas aqui estamos desenvolvendo as <strong>SUAS habilidades e competências empreendedoras</strong>.
              </p>
            </div>

            <div>
              <label class="block text-xs font-bold text-zinc-200 mb-1 text-justify">
                <strong>8. Percepção Empreendedora do Cenário:</strong> Qual a sua percepção técnica e comercial diante desse ambiente? Você manteria apenas a solução de Link Duplo que o João apresentou no Módulo C ou faria uma nova proposta? Justifique sua decisão focando na garantia do serviço prestado.
              </label>
              <textarea [(ngModel)]="modD_q8" rows="4" placeholder="Sua justificativa..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
            </div>

            <div>
              <label class="block text-xs font-bold text-zinc-200 mb-1 text-justify">
                <strong>9. Diagnóstico de Risco na Camada Física:</strong> Explique, com visão de negócios, por que instalar um roteador avançado e um link de backup no meio desse "ninho de ratos" pode <strong>não resolver totalmente</strong> os problemas de paralisação e falhas internas da Logística Expressa.
              </label>
              <textarea [(ngModel)]="modD_q9" rows="3" placeholder="Sua explicação de risco..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
            </div>

            <div class="space-y-2">
              <label class="block text-xs font-bold text-zinc-200 text-justify">
                <strong>10. A Solução Técnica e Profissionalização da Infraestrutura:</strong> Caso sua equipe decida propor a reorganização do cabeamento estruturado da empresa, cite pelo menos <strong>três componentes físicos</strong> que você deve incluir no orçamento de infraestrutura para eliminar a bagunça de vez (Ex: Mini-Rack...):
              </label>
              <div class="space-y-2">
                <div class="flex items-center space-x-2">
                  <span class="text-xs font-bold text-[#F27D26] font-mono">1.</span>
                  <input type="text" [(ngModel)]="modD_q10_1" placeholder="Componente 1 (ex: Mini-Rack 19'')..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-[#F27D26]">
                </div>
                <div class="flex items-center space-x-2">
                  <span class="text-xs font-bold text-[#F27D26] font-mono">2.</span>
                  <input type="text" [(ngModel)]="modD_q10_2" placeholder="Componente 2 (ex: Patch Panel / Organizador)..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-[#F27D26]">
                </div>
                <div class="flex items-center space-x-2">
                  <span class="text-xs font-bold text-[#F27D26] font-mono">3.</span>
                  <input type="text" [(ngModel)]="modD_q10_3" placeholder="Componente 3 (ex: Patch Cords Certificados)..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-2 text-xs text-white focus:outline-none focus:border-[#F27D26]">
                </div>
              </div>
            </div>

            <div>
              <label class="block text-xs font-bold text-zinc-200 mb-1 text-justify">
                <strong>11. Quebrando Objeções (Argumentação de Valor):</strong> Clientes leigos costumam reagir dizendo: <em>"Mas por que vou pagar para você organizar esses fios se a rede interna já está funcionando?"</em>.<br><br>Como sua equipe responderia a essa objeção do Sr. Roberto, focando na <strong>redução do tempo de manutenção (troubleshooting)</strong> e na <strong>prevenção de prejuízos operacionais</strong>?
              </label>
              <textarea [(ngModel)]="modD_q11" rows="4" placeholder="Sua resposta ao cliente..." class="w-full bg-[#0A0A0B] border border-white/10 rounded-lg p-3 text-xs text-white focus:outline-none focus:border-[#F27D26]"></textarea>
            </div>
          </div>
        </div>
      }

      <!-- FULLSCREEN IMMERSIVE PRESENTATION OVERLAY MODAL (GAMMA APP / POWERPOINT STYLE) -->
      @if (isPresentationModalOpen()) {
        <div class="fixed inset-0 z-[100] bg-[#0A0A0B] backdrop-blur-2xl p-4 sm:p-8 flex flex-col justify-between select-none overflow-hidden h-screen w-screen max-h-screen max-w-screen animate-in fade-in zoom-in-95 duration-200">
          
          <!-- Top Bar inside Overlay -->
          <div class="max-w-6xl w-full mx-auto flex items-center justify-between pb-4 border-b border-white/10 text-white">
            <div class="flex items-center space-x-3">
              <span class="w-9 h-9 rounded-xl bg-[#F27D26]/20 border border-[#F27D26]/40 text-[#F27D26] font-bold flex items-center justify-center">
                <span class="material-symbols-outlined text-xl">slideshow</span>
              </span>
              <div>
                <h3 class="text-sm font-bold text-white tracking-wide">Aula 2: A Jornada do Empreendedor em TI</h3>
              </div>
            </div>
          </div>

          <!-- Centered 16:9 Presentation Canvas Container -->
          <div class="max-w-6xl w-full mx-auto my-auto bg-[#121214] border border-white/15 rounded-3xl p-6 sm:p-12 shadow-2xl shadow-black/90 min-h-[460px] sm:min-h-[540px] flex flex-col justify-center relative overflow-hidden">
            <div class="absolute top-0 right-0 w-96 h-96 bg-[#F27D26]/10 rounded-full blur-3xl pointer-events-none"></div>

            <!-- SLIDES CONTENT IN OVERLAY -->
            <div class="my-auto">
              <!-- SLIDE 1 -->
              @if (currentSlide() === 0) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">school</span>
                    <span>AULA 2 • EMPREENDEDORISMO EM REDES</span>
                  </span>

                  <div>
                    <h1 class="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-tight">
                      A Jornada do Empreendedor em TI
                    </h1>
                    <p class="text-lg sm:text-xl text-zinc-400 font-medium mt-2">
                      Como o conhecimento técnico resolve problemas reais de mercado.
                    </p>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-2xl p-6 sm:p-8 border-l-4 border-amber-500 border-y border-r border-white/10 space-y-3 shadow-xl">
                    <h3 class="text-xl font-bold text-white flex items-center space-x-2">
                      <span>🙋‍♂️</span>
                      <span>Conheça o João:</span>
                    </h3>
                    <p class="text-zinc-300 text-sm sm:text-base leading-relaxed">
                      O João vai se formar em <strong>Técnico em Redes de Computadores</strong> no fim do ano. Mas já domina crimpagem, MikroTik, Switches e Windows. Hoje ele recebeu uma ligação do <strong>Sr. Roberto</strong>, dono da <em>Logística Expressa</em>, dizendo em desespero: <em class="text-amber-400 font-semibold">"Minha internet caiu e estou perdendo R$ 1.500 por hora!"</em>
                    </p>
                  </div>

                  <p class="text-zinc-300 text-sm sm:text-base leading-relaxed">
                    <strong>Como o João pode ajudar?</strong> Acompanhe os slides e veja como transformar esse problema em uma oportunidade de negócio!
                  </p>
                </div>
              }

              <!-- SLIDE 2 -->
              @if (currentSlide() === 1) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">search</span>
                    <span>PASSO 1 • O DIAGNÓSTICO</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    O Dilema do Sr. Roberto
                  </h2>

                  <div class="bg-indigo-950/50 rounded-2xl p-6 border border-indigo-500/40 space-y-2 shadow-xl">
                    <h3 class="text-base sm:text-lg font-bold text-indigo-300 flex items-center space-x-2">
                      <span class="material-symbols-outlined text-indigo-400 text-xl">help</span>
                      <span>Pergunta de Reflexão Rápida:</span>
                    </h3>
                    <p class="text-zinc-200 text-sm sm:text-base leading-relaxed">
                      A internet da Logística Expressa cai toda terça e quinta. Os caminhões ficam parados no pátio porque a Nota Fiscal Eletrônica não emite. Qual é o REAL problema que precisa ser resolvido?
                    </p>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-red-500 border-y border-r border-white/10 space-y-3">
                      <h3 class="text-base sm:text-lg font-bold text-red-400 flex items-center space-x-2">
                        <span>❌</span>
                        <span>Visão Reativa (Técnico Tradicional)</span>
                      </h3>
                      <p class="text-sm sm:text-base italic text-zinc-300">
                        "Vou lá reiniciar o modem residencial da operadora e trocar a senha do Wi-Fi."
                      </p>
                      <p class="text-xs sm:text-sm text-zinc-400 pt-3 border-t border-white/5">
                        Resultado: Na terça seguinte a rede cai de novo e a empresa perde mais R$ 3.000.
                      </p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-3">
                      <h3 class="text-base sm:text-lg font-bold text-emerald-400 flex items-center space-x-2">
                        <span>✅</span>
                        <span>Visão Empreendedora (O João)</span>
                      </h3>
                      <p class="text-sm sm:text-base italic text-zinc-300">
                        "Preciso garantir que a emissão de notas NUNCA pare, criando tolerância a falhas na rede."
                      </p>
                      <p class="text-xs sm:text-sm text-zinc-400 pt-3 border-t border-white/5">
                        Resultado: O cliente economiza milhares de reais e fecha um contrato mensal de suporte.
                      </p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 3 -->
              @if (currentSlide() === 2) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">key</span>
                    <span>PASSO 2 • CONCEITO CHAVE</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    O que é Empreender em Redes?
                  </h2>

                  <p class="text-zinc-300 text-sm sm:text-base leading-relaxed">
                    Empreender NÃO é apenas abrir uma empresa. É a habilidade de conectar uma <strong>DOR REAL DE NEGÓCIO</strong> a uma <strong>SOLUÇÃO DE INFRAESTRUTURA</strong>.
                  </p>

                  <div class="bg-[#F27D26]/10 rounded-2xl p-5 border border-[#F27D26]/40 text-center font-mono text-sm sm:text-base font-bold text-[#F27D26] leading-relaxed shadow-lg">
                    DOR DO CLIENTE (Paralisação/Prejuízo) ➔ SOLUÇÃO DE REDE (Failover/Redundância) = PROPOSTA DE VALOR 💰
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-white/10 space-y-3">
                      <h3 class="text-base sm:text-lg font-bold text-white flex items-center space-x-2">
                        <span>🍕</span>
                        <span>Exemplo Fora da TI (Pizzaria)</span>
                      </h3>
                      <p class="text-xs sm:text-sm text-zinc-300"><strong>Dor:</strong> Pizzas chegam frias e atrasadas no sábado.</p>
                      <p class="text-xs sm:text-sm text-zinc-300"><strong>Solução:</strong> Bag térmica com aquecimento USB e roteamento de entregadores via App.</p>
                      <p class="text-xs sm:text-sm text-emerald-400 font-semibold pt-2 border-t border-white/5"><strong>Valor:</strong> Pizza quente = Cliente satisfeito e fiel.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-white/10 space-y-3">
                      <h3 class="text-base sm:text-lg font-bold text-white flex items-center space-x-2">
                        <span>🌐</span>
                        <span>Exemplo em Redes (Supermercado)</span>
                      </h3>
                      <p class="text-xs sm:text-sm text-zinc-300"><strong>Dor:</strong> Caixas travam ao passar compras no horário de pico.</p>
                      <p class="text-xs sm:text-sm text-zinc-300"><strong>Solução:</strong> Roteador MikroTik com link duplo (Fibra + Chip 4G) e VLAN isolada para os Caixas.</p>
                      <p class="text-xs sm:text-sm text-emerald-400 font-semibold pt-2 border-t border-white/5"><strong>Valor:</strong> Fila rápida = Zero perda de vendas.</p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 4 -->
              @if (currentSlide() === 3) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">psychology</span>
                    <span>PASSO 3 • MENTALIDADE</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Qual Profissional Você Quer Ser?
                  </h2>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-white/10 space-y-4">
                      <div class="text-4xl">🛠️</div>
                      <h3 class="text-lg sm:text-xl font-bold text-white">O Técnico Operacional</h3>
                      <ul class="space-y-2.5 text-xs sm:text-sm text-zinc-400">
                        <li class="flex items-start space-x-2">
                          <span class="text-zinc-500">•</span>
                          <span>Foca apenas no <strong>Equipamento / Cabo</strong>.</span>
                        </li>
                        <li class="flex items-start space-x-2">
                          <span class="text-zinc-500">•</span>
                          <span>Espera o chamado abrir para consertar.</span>
                        </li>
                        <li class="flex items-start space-x-2">
                          <span class="text-zinc-500">•</span>
                          <span>Cobra por hora de visita (Ganho único).</span>
                        </li>
                        <li class="flex items-start space-x-2">
                          <span class="text-zinc-500">•</span>
                          <span>Diz: <em>"O problema é da sua operadora."</em></span>
                        </li>
                      </ul>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border border-[#F27D26]/60 space-y-4 shadow-xl shadow-[#F27D26]/10">
                      <div class="text-4xl">🚀</div>
                      <h3 class="text-lg sm:text-xl font-bold text-[#F27D26]">O Especialista Empreendedor</h3>
                      <ul class="space-y-2.5 text-xs sm:text-sm text-zinc-200">
                        <li class="flex items-start space-x-2">
                          <span class="text-[#F27D26]">•</span>
                          <span>Foca na <strong>Continuidade do Negócio</strong>.</span>
                        </li>
                        <li class="flex items-start space-x-2">
                          <span class="text-[#F27D26]">•</span>
                          <span>Monitora proativamente para evitar paradas.</span>
                        </li>
                        <li class="flex items-start space-x-2">
                          <span class="text-[#F27D26]">•</span>
                          <span>Cobra um valor mensal fixo (Contrato MSP).</span>
                        </li>
                        <li class="flex items-start space-x-2">
                          <span class="text-[#F27D26]">•</span>
                          <span>Diz: <em>"Instalei um backup automático para você não parar."</em></span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 5 -->
              @if (currentSlide() === 4) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">account_tree</span>
                    <span>PASSO 4 • A SOLUÇÃO TÉCNICA DO JOÃO</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    Visualizando a Transformação da Rede
                  </h2>
                  <p class="text-zinc-400 text-xs sm:text-base">
                    Veja como o João desenhou a solução técnica para a <em>Logística Expressa</em>:
                  </p>

                  <div class="space-y-4">
                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-red-500 border-y border-r border-white/10 space-y-3">
                      <h3 class="text-base sm:text-lg font-bold text-red-400">❌ Rede Antiga (Ponto Único de Falha):</h3>
                      <div class="bg-[#121214] p-4 rounded-xl border border-red-500/30 font-mono text-xs sm:text-sm text-red-300 text-center">
                        [ Modem Residencial ] ── (Wi-Fi Comum) ──> [ Caixas, Escritório e Celulares Juntos ]
                      </div>
                      <p class="text-xs sm:text-sm text-zinc-400">Se a operadora cai ou o Wi-Fi engasga, a empresa inteira para!</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-emerald-500 border-y border-r border-white/10 space-y-3">
                      <h3 class="text-base sm:text-lg font-bold text-emerald-400">✅ Nova Arquitetura Proposta pelo João (Tolerante a Falhas):</h3>
                      <div class="bg-[#121214] p-4 rounded-xl border border-emerald-500/30 font-mono text-xs sm:text-sm text-emerald-300 text-center leading-relaxed">
                        [ Link 1: Fibra ] ──┐<br>
                        &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;├─> [ Roteador MikroTik / Failover ] ──> [ VLAN NFs ] + [ Wi-Fi Visitantes ]<br>
                        [ Link 2: 4G/5G ] ──┘
                      </div>
                      <p class="text-xs sm:text-sm text-zinc-400">Se a Fibra cair, o 4G assume em 2 segundos de forma invisível!</p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 6 -->
              @if (currentSlide() === 5) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">map</span>
                    <span>PASSO 5 • MAPA DE OPORTUNIDADES</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    6 Caminhos para Lucrar com Redes
                  </h2>

                  <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2">
                      <div class="text-3xl">🌐</div>
                      <h3 class="font-bold text-white text-base">1. ISPs Regionais</h3>
                      <p class="text-xs sm:text-sm text-zinc-400 leading-relaxed">Provedores de Fibra/Rádio e redes neutras em bairros/zonas rurais.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2">
                      <div class="text-3xl">☁️</div>
                      <h3 class="font-bold text-white text-base">2. Cloud & VPNs</h3>
                      <p class="text-xs sm:text-sm text-zinc-400 leading-relaxed">Migração de servidores para nuvem e conexão segura para Home Office.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2">
                      <div class="text-3xl">🛡️</div>
                      <h3 class="font-bold text-white text-base">3. Cibersegurança</h3>
                      <p class="text-xs sm:text-sm text-zinc-400 leading-relaxed">Firewalls corporativos, proteção contra ransomware e LGPD.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2">
                      <div class="text-3xl">🤖</div>
                      <h3 class="font-bold text-white text-base">4. IoT & Câmeras IP</h3>
                      <p class="text-xs sm:text-sm text-zinc-400 leading-relaxed">Redes para automação industrial, agronegócio e CFTV IP.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2">
                      <div class="text-3xl">📡</div>
                      <h3 class="font-bold text-white text-base">5. Wi-Fi 6 Corporativo</h3>
                      <p class="text-xs sm:text-sm text-zinc-400 leading-relaxed">Projetos para hotéis, eventos, galpões e coworkings.</p>
                    </div>

                    <div class="bg-[#0A0A0B] rounded-2xl p-5 border border-white/10 space-y-2">
                      <div class="text-3xl">🔄</div>
                      <h3 class="font-bold text-white text-base">6. Contratos MSP</h3>
                      <p class="text-xs sm:text-sm text-zinc-400 leading-relaxed">Gerenciamento mensal da TI de empresas com receita recorrente.</p>
                    </div>
                  </div>
                </div>
              }

              <!-- SLIDE 7 -->
              @if (currentSlide() === 6) {
                <div class="space-y-6 animate-in fade-in duration-300">
                  <span class="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-[#F27D26]/10 text-[#F27D26] text-xs font-mono font-bold border border-[#F27D26]/30 uppercase tracking-wider">
                    <span class="material-symbols-outlined text-sm">verified</span>
                    <span>SÍNTESE DA AULA</span>
                  </span>

                  <h2 class="text-2xl sm:text-4xl font-extrabold text-white tracking-tight">
                    A Grande Lição do Dia
                  </h2>

                  <div class="bg-gradient-to-r from-[#F27D26]/20 via-[#F27D26]/10 to-transparent rounded-2xl p-6 sm:p-10 border border-[#F27D26]/50 text-center shadow-2xl">
                    <h3 class="text-xl sm:text-2xl lg:text-3xl font-extrabold text-[#F27D26] leading-relaxed">
                      "Empreender em Redes não é vender equipamentos, cabos ou configurações.<br><br>É resolver problemas que fazem alguém perder tempo, dinheiro ou oportunidades."
                    </h3>
                  </div>

                  <div class="bg-[#0A0A0B] rounded-2xl p-6 border-l-4 border-[#F27D26] border-y border-r border-white/10 space-y-4 shadow-xl">
                    <h3 class="text-lg font-bold text-white flex items-center space-x-2">
                      <span>🚀</span>
                      <span>Próximo Passo:</span>
                    </h3>
                    <p class="text-zinc-300 text-xs sm:text-base leading-relaxed">
                      Agora é a sua vez! Abra a <strong>Folha de Atividades</strong>, leia o estudo de caso completo, cote os equipamentos reais na internet, resolva o desafio do cabeamento físico e monte a proposta comercial para o Sr. Roberto!
                    </p>
                    <div>
                      <button 
                        type="button" 
                        (click)="closePresentationModal(); onSelectView('activity')" 
                        class="px-5 py-2.5 rounded-xl bg-[#F27D26] text-black font-bold text-xs sm:text-sm hover:bg-[#e06c17] transition-all flex items-center space-x-2 cursor-pointer shadow-lg shadow-[#F27D26]/20">
                        <span class="material-symbols-outlined text-base">assignment</span>
                        <span>Ir para Folha de Atividades</span>
                      </button>
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>

          <!-- Bottom Floating Control Dock (Gamma Style) -->
          <div class="max-w-xl w-full mx-auto bg-[#121214]/90 backdrop-blur border border-white/15 rounded-2xl p-2.5 shadow-2xl flex items-center justify-between space-x-4">
            <button 
              type="button" 
              (click)="prevSlide()" 
              [disabled]="currentSlide() === 0"
              class="px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 disabled:opacity-30 disabled:hover:bg-white/5 text-xs font-semibold text-white border border-white/10 transition-all flex items-center space-x-1 cursor-pointer">
              <span class="material-symbols-outlined text-base">chevron_left</span>
              <span class="hidden sm:inline">Anterior</span>
            </button>

            <!-- Interactive Slide Jump Buttons -->
            <div class="flex items-center space-x-1.5">
              @for (slideNum of slideNumbers; track slideNum) {
                <button 
                  type="button" 
                  (click)="goToSlide(slideNum)"
                  [class]="currentSlide() === slideNum 
                    ? 'bg-[#F27D26] text-black font-bold scale-110 shadow-md shadow-[#F27D26]/30' 
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'"
                  class="w-7 h-7 rounded-lg text-xs font-mono transition-all flex items-center justify-center cursor-pointer">
                  {{ slideNum + 1 }}
                </button>
              }
            </div>

            <button 
              type="button" 
              (click)="nextSlide()" 
              [disabled]="currentSlide() === totalSlides - 1"
              class="px-3.5 py-2 rounded-xl bg-[#F27D26] hover:bg-[#e06c17] disabled:opacity-30 disabled:hover:bg-[#F27D26] text-xs font-bold text-black transition-all flex items-center space-x-1 cursor-pointer">
              <span class="hidden sm:inline">Próximo</span>
              <span class="material-symbols-outlined text-base">chevron_right</span>
            </button>
          </div>
        </div>
      }
    </section>
  `
})
export class Aula2SectionComponent {
  navigateBack = output<void>();

  activeView = signal<'slides' | 'activity'>('slides');
  currentSlide = signal<number>(0);
  totalSlides = 7;
  slideNumbers = [0, 1, 2, 3, 4, 5, 6];

  isPresentationModalOpen = signal<boolean>(false);
  isNativeFullscreen = signal<boolean>(false);

  // Activity Form Models
  modA_q1_p1 = '';
  modA_q1_p2 = '';
  modA_q2_p1 = '';
  modA_q2_p2 = '';
  modB_q2 = '';
  modB_q3 = '';
  modB_q4 = '';
  modC_q5 = '';
  priceEq1 = '';
  priceEq2 = '';
  priceTotal = '';
  modC_q7 = '';
  modD_q8 = '';
  modD_q9 = '';
  modD_q10_1 = '';
  modD_q10_2 = '';
  modD_q10_3 = '';
  modD_q11 = '';

  onSelectView(view: 'slides' | 'activity') {
    this.activeView.set(view);
    if (view === 'slides') {
      this.openPresentationModal();
    }
  }

  nextSlide() {
    if (this.currentSlide() < this.totalSlides - 1) {
      this.currentSlide.update(v => v + 1);
    }
  }

  prevSlide() {
    if (this.currentSlide() > 0) {
      this.currentSlide.update(v => v - 1);
    }
  }

  goToSlide(slideIndex: number) {
    if (slideIndex >= 0 && slideIndex < this.totalSlides) {
      this.currentSlide.set(slideIndex);
    }
  }

  openPresentationModal() {
    this.isPresentationModalOpen.set(true);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().then(() => {
          this.isNativeFullscreen.set(true);
        }).catch(() => {});
      }
    }
  }

  closePresentationModal() {
    this.isPresentationModalOpen.set(false);
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
      if (document.fullscreenElement) {
        document.exitFullscreen().then(() => {
          this.isNativeFullscreen.set(false);
        }).catch(() => {});
      }
    }
  }

  toggleNativeFullscreen() {
    if (typeof document !== 'undefined') {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().then(() => {
          this.isNativeFullscreen.set(true);
        }).catch(() => {});
      } else {
        document.exitFullscreen().then(() => {
          this.isNativeFullscreen.set(false);
        }).catch(() => {});
      }
    }
  }

  printActivity() {
    if (typeof window !== 'undefined') {
      window.print();
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (this.isPresentationModalOpen() || this.activeView() === 'slides') {
      if (event.key === 'ArrowRight' || event.key === ' ') {
        this.nextSlide();
      } else if (event.key === 'ArrowLeft') {
        this.prevSlide();
      } else if (event.key === 'Escape') {
        this.closePresentationModal();
      }
    }
  }
}
